import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ContactGroupSummary,
  ContactImportBirthdayFormat,
  ContactImportCapacityPreview,
  ContactImportColumnMapping,
  ContactImportJobDto,
} from "@multi-chat/shared";
import {
  COUNTRY_DIAL_CODES,
  COUNTRY_NAMES_HE,
  contactImportMappingHasTarget,
  isContactImportJobExecutionStatus,
  isContactImportJobPreviewStatus,
  isContactImportJobTerminalStatus,
  shouldPollContactImportJob,
  suggestContactImportMapping,
} from "@multi-chat/shared";
import {
  ArrowDownToLine,
  Ban,
  ChevronDown,
  CircleCheck,
  CircleX,
  FileUp,
  Info,
  LoaderCircle,
  Search,
} from "lucide-react";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import { Button } from "../../../components/ui/button/index.js";
import { AlertDialog, AlertDialogTitle } from "../../../components/ui/alert-dialog/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover/index.js";
import {
  cancelContactImportJob,
  confirmContactImportJob,
  previewContactImportJobCapacity,
  contactImportTemplateUrl,
  getActiveContactImportJob,
  getContactImportJob,
  uploadContactImportFile,
} from "../../../api/contacts-api.js";
import {
  contactImportCanExecute,
  contactImportQuotaExceeded,
  contactLimitFeedback,
  importJobShowsResultCounters,
  isContactLimitReachedError,
  mapContactError,
} from "../contact-errors.js";
import {
  contactImportDialogDismissAction,
  contactImportDialogView,
  decideContactImportOpenSession,
  shouldShowContactImportJobCancel,
  trustInMemoryContactImportDialogJob,
} from "../contact-import-dialog-view.js";
import { activeImportWizardStep, type ImportWizardStep } from "../import-wizard.js";
import { dispatchContactImportWatch } from "../contact-import-polling.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";
import { cn } from "../../../lib/utils.js";
import { useOptionalNotifications } from "../../notifications/notification-context.js";
import {
  emptyContactGroupSelection,
  type ContactGroupSelectionValue,
} from "../contact-group-selection-value.js";
import { ImportContactsMappingStep } from "./ImportContactsMappingStep.js";
import { ImportContactsOutcomePanel } from "./ImportContactsOutcomePanel.js";
import { ImportContactsReviewStep } from "./ImportContactsReviewStep.js";
import { ImportContactsStepper } from "./ImportContactsStepper.js";
import { ImportSelectedFileBar } from "./ImportSelectedFileBar.js";
import {
  contactImportJobPercent,
  ImportJobResultStats,
} from "./ImportJobResultStats.js";
import { ImportFileMetadata } from "./ImportFileMetadata.js";

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

function SettingHelp({
  label,
  body,
}: {
  label: string;
  body: string;
}): React.JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-grid size-8 shrink-0 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]"
          aria-label={label}
        >
          <Info size={15} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 text-start text-[0.75rem] leading-snug">
        {body}
      </PopoverContent>
    </Popover>
  );
}

function CountryIsoSelector({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected =
    COUNTRY_DIAL_CODES.find((country) => country.code === value) ??
    COUNTRY_DIAL_CODES.find((country) => country.code === "IL");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter((country) => {
      const hebrewName = COUNTRY_NAMES_HE[country.code] ?? "";
      return (
        country.name.toLowerCase().includes(query) ||
        hebrewName.toLowerCase().includes(query) ||
        country.dialCode.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    });
  }, [search]);

  const selectedLabel = selected
    ? isHebrewUi
      ? (COUNTRY_NAMES_HE[selected.code] ?? selected.name)
      : selected.name
    : value;

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex h-9 w-full max-w-64 items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-start text-[0.8125rem] disabled:opacity-70"
          data-testid="import-country-selector"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span aria-hidden="true">{selected ? flagEmoji(selected.code) : ""}</span>
            <span className="min-w-0 truncate">{selectedLabel}</span>
            {selected ? (
              <span className="shrink-0 tabular-nums text-muted [direction:ltr]" dir="ltr">
                {selected.dialCode}
              </span>
            ) : null}
          </span>
          <ChevronDown size={12} className="shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line px-2 py-1.5">
          <Search size={13} className="shrink-0 text-muted" aria-hidden="true" />
          <input
            className="min-w-0 flex-1 border-0 bg-transparent text-[0.75rem] outline-none"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("phoneField.countrySearch")}
          />
        </div>
        <div className="max-h-[17.5rem] overflow-y-auto overscroll-contain p-1">
          {filtered.map((country) => {
            const hebrewName = isHebrewUi ? COUNTRY_NAMES_HE[country.code] : undefined;
            return (
              <button
                key={country.code}
                type="button"
                className={cn(
                  "flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1 text-start hover:bg-surface-muted",
                  country.code === selected?.code ? "bg-surface-muted" : null,
                )}
                onClick={() => {
                  onChange(country.code);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <span aria-hidden="true">{flagEmoji(country.code)}</span>
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="truncate text-[0.75rem]">{country.name}</span>
                  {hebrewName ? (
                    <span className="truncate text-[0.6875rem] text-muted">{hebrewName}</span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums text-[0.75rem] text-muted [direction:ltr]" dir="ltr">
                  {country.dialCode}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ImportContactsDialog({
  open,
  organizationId,
  groups,
  restoreJobId,
  onClose,
  onClearRestoreJobId,
  onJobStarted,
  onGroupsChanged,
}: {
  open: boolean;
  organizationId: number;
  groups: ContactGroupSummary[];
  restoreJobId?: string | null;
  onClose: () => void;
  onClearRestoreJobId?: () => void;
  onJobStarted?: () => void;
  onGroupsChanged?: () => void | Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const notifications = useOptionalNotifications();
  const hideImportProgressToast = notifications?.hideImportProgressToast;
  const revealImportProgressToast = notifications?.revealImportProgressToast;
  const upsertImportNotification = notifications?.upsertImportNotification;
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingProgressToastRevealRef = useRef<ContactImportJobDto | null>(null);
  const draftEpochRef = useRef(0);
  const abandoningDraftRef = useRef(false);
  const [returnedToSetup, setReturnedToSetup] = useState(false);
  const [defaultCountryIso, setDefaultCountryIso] = useState("IL");
  const [autoLink, setAutoLink] = useState(false);
  const [job, setJob] = useState<ContactImportJobDto | null>(null);
  const [boundJobId, setBoundJobId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [headers, setHeaders] = useState<string[]>([]);
  const [samples, setSamples] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ContactImportColumnMapping>([]);
  const [birthdayFormat, setBirthdayFormat] = useState<ContactImportBirthdayFormat>("auto");
  const [applyGroups, setApplyGroups] = useState<ContactGroupSelectionValue>(emptyContactGroupSelection());
  const [loading, setLoading] = useState(false);
  const [previewPending, setPreviewPending] = useState(false);
  const [activeCheckSettled, setActiveCheckSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capacity, setCapacity] = useState<ContactImportCapacityPreview | null>(null);
  const [capacityAutoLink, setCapacityAutoLink] = useState(false);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const capacityRef = useRef<ContactImportCapacityPreview | null>(null);
  const previewScopeRef = useRef<string | null>(null);
  capacityRef.current = capacity;
  const [wizardStep, setWizardStep] = useState<ImportWizardStep>("file");
  const stepTitleRef = useRef<HTMLHeadingElement>(null);
  const [discardRestoreJob, setDiscardRestoreJob] = useState(false);
  const effectiveRestoreJobId = discardRestoreJob ? null : (restoreJobId ?? null);

  const trustInMemoryJob = trustInMemoryContactImportDialogJob(
    job?.status ?? null,
    job?.id ?? null,
    effectiveRestoreJobId,
    activeCheckSettled,
    boundJobId,
  );
  const view = contactImportDialogView({
    jobStatus: trustInMemoryJob ? (job?.status ?? null) : null,
    jobId: trustInMemoryJob ? (job?.id ?? null) : null,
    restoreJobId: effectiveRestoreJobId,
    activeJobCheckPending: open && !trustInMemoryJob && !activeCheckSettled,
    previewPending,
  });
  const dialogView = returnedToSetup && view === "map" ? "setup" : view;
  const showCancelRemaining = shouldShowContactImportJobCancel(dialogView, job?.status ?? null);
  const wizard = activeImportWizardStep(dialogView, wizardStep);
  const preparedAwaiting = job?.status === "AWAITING_CONFIRMATION";
  const quotaExceeded = contactImportQuotaExceeded(capacity, capacityLoading);
  const canExecuteImport = contactImportCanExecute(capacity, capacityLoading);
  const settledCanExecute = contactImportCanExecute(capacity, false);
  const phoneUnmapped = !mapping.some((entry) => entry.target === "phone");
  const birthdayChoiceMissing =
    mapping.some((entry) => entry.target === "birthday") &&
    birthdayFormat === "auto" &&
    capacity?.birthdayImport?.ambiguous === true;
  const countryLabel = useMemo(() => {
    const selected = COUNTRY_DIAL_CODES.find((country) => country.code === defaultCountryIso);
    if (!selected) return defaultCountryIso;
    return i18n.language === "he"
      ? (COUNTRY_NAMES_HE[selected.code] ?? selected.name)
      : selected.name;
  }, [defaultCountryIso, i18n.language]);

  useEffect(() => {
    if (!open || dialogView !== "map" || !job || !contactImportMappingHasTarget(mapping)) {
      previewScopeRef.current = null;
      setCapacity(null);
      setCapacityLoading(false);
      return;
    }
    const scope = `${job.id}:${birthdayFormat}:${JSON.stringify(mapping)}`;
    const refreshInPlace = previewScopeRef.current === scope && capacityRef.current != null;
    previewScopeRef.current = scope;
    const controller = new AbortController();
    const requestedAutoLink = autoLink;
    if (!refreshInPlace) {
      setCapacity(null);
    }
    setCapacityLoading(true);
    const timer = window.setTimeout(() => {
      void previewContactImportJobCapacity(
        organizationId,
        job.id,
        {
          columnMapping: mapping,
          birthdayFormat,
          defaultCountryIso,
          autoLinkExactPhoneMatches: requestedAutoLink,
        },
        controller.signal,
      )
        .then((next) => {
          if (!controller.signal.aborted) {
            setCapacity(next);
            setCapacityAutoLink(requestedAutoLink);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setCapacity(null);
        })
        .finally(() => {
          if (!controller.signal.aborted) setCapacityLoading(false);
        });
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [open, dialogView, organizationId, job, mapping, birthdayFormat, defaultCountryIso, autoLink]);

  function applyJobToForm(next: ContactImportJobDto): void {
    const wasTerminal = job != null && isContactImportJobTerminalStatus(job.status);
    setJob(next);
    setBoundJobId(next.id);
    setFileName(next.originalFileName);
    setRowCount(next.totalRows ?? 0);
    setCapacity(null);
    setCapacityAutoLink(false);
    setCapacityLoading(false);
    const nextHeaders = next.headers ?? [];
    setHeaders(nextHeaders);
    const sampleRow = next.preview?.sampleRows[0] ?? [];
    setSamples(nextHeaders.map((_, index) => sampleRow[index] ?? ""));
    setMapping(
      next.columnMapping && next.columnMapping.length > 0
        ? next.columnMapping
        : next.suggestedMapping ?? suggestContactImportMapping(nextHeaders),
    );
    setBirthdayFormat(next.birthdayFormat ?? "auto");
    if (next.defaultCountryIso) setDefaultCountryIso(next.defaultCountryIso);
    if (!isContactImportJobPreviewStatus(next.status)) {
      setAutoLink(next.autoLinkExactPhoneMatches);
    }
    if (next.applyGroupIds.length > 0) {
      setApplyGroups({ groupIds: next.applyGroupIds, newGroupNames: [] });
    }
    if (
      next.status === "AWAITING_CONFIRMATION" ||
      isContactImportJobExecutionStatus(next.status) ||
      isContactImportJobTerminalStatus(next.status)
    ) {
      setPreviewPending(false);
      setReturnedToSetup(false);
    } else if (next.status === "UPLOADED" || next.status === "PREPARING") {
      setPreviewPending(true);
    }
    if (isContactImportJobTerminalStatus(next.status) && !wasTerminal) {
      window.dispatchEvent(new Event("contact-import-updated"));
    }
  }

  function resetDraftState(): void {
    draftEpochRef.current += 1;
    setReturnedToSetup(false);
    setDefaultCountryIso("IL");
    setAutoLink(false);
    setJob(null);
    setBoundJobId(null);
    setFileName(null);
    setUploadingFileName(null);
    setRowCount(0);
    setHeaders([]);
    setSamples([]);
    setMapping([]);
    setBirthdayFormat("auto");
    setWizardStep("file");
    setApplyGroups(emptyContactGroupSelection());
    setCapacity(null);
    setCapacityAutoLink(false);
    setCapacityLoading(false);
    setLoading(false);
    setPreviewPending(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  useEffect(() => {
    if (!open) {
      setActiveCheckSettled(false);
      setDiscardRestoreJob(false);
      return;
    }
    if (trustInMemoryJob) {
      setActiveCheckSettled(true);
    }
    let cancelled = false;
    void (async () => {
      try {
        const restoredJobPromise = effectiveRestoreJobId
          ? getContactImportJob(organizationId, effectiveRestoreJobId)
              .catch(() => getContactImportJob(organizationId, effectiveRestoreJobId))
              .catch(() => null)
          : Promise.resolve(null);
        const [restoredJob, active] = await Promise.all([
          restoredJobPromise,
          getActiveContactImportJob(organizationId),
        ]);
        if (cancelled) return;
        const decision = decideContactImportOpenSession({
          restoreJobId: effectiveRestoreJobId,
          restoredJob,
          activeJob: active.job,
        });
        if (decision.action === "apply") {
          applyJobToForm(decision.job);
        } else if (decision.action === "reset") {
          resetDraftState();
        }
      } catch {
        if (!cancelled && effectiveRestoreJobId == null) {
          resetDraftState();
        }
      } finally {
        if (!cancelled) setActiveCheckSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, organizationId, effectiveRestoreJobId]);

  useEffect(() => {
    if (!open || !job || !shouldPollContactImportJob(job.status)) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const latest = await getContactImportJob(organizationId, job.id);
          if (cancelled) return;
          applyJobToForm(latest);
        } catch {
          /* next tick */
        }
      })();
    }, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, job?.id, job?.status, organizationId]);

  useEffect(() => {
    if (!open || !job || !isContactImportJobExecutionStatus(job.status)) return;
    hideImportProgressToast?.(job.id);
  }, [open, job?.id, job?.status, hideImportProgressToast]);

  useEffect(() => {
    if (open) return;
    const pending = pendingProgressToastRevealRef.current;
    if (!pending || !isContactImportJobExecutionStatus(pending.status)) return;
    pendingProgressToastRevealRef.current = null;
    revealImportProgressToast?.(pending.id);
    upsertImportNotification?.({
      jobId: pending.id,
      message: t("contacts.import.job.progressToast"),
      durationMs: null,
      processedRows: pending.processedRows,
      totalRows: pending.totalRows,
      createdCount: pending.createdCount,
      restoredCount: pending.restoredCount,
      skippedCount: pending.skippedCount,
      failedCount: pending.failedCount,
      status: pending.status,
    });
    dispatchContactImportWatch(pending.id);
  }, [open, revealImportProgressToast, upsertImportNotification, t]);

  async function abandonDraftThen(
    draft: ContactImportJobDto,
    after?: () => void,
  ): Promise<void> {
    if (abandoningDraftRef.current) return;
    abandoningDraftRef.current = true;
    setLoading(true);
    setError(null);
    try {
      await cancelContactImportJob(organizationId, draft.id);
    } catch (err) {
      setError(mapContactError(t, err, t("contacts.import.error")));
      setLoading(false);
      abandoningDraftRef.current = false;
      return;
    }
    resetDraftState();
    abandoningDraftRef.current = false;
    after?.();
  }

  function handleClose(nextJob?: ContactImportJobDto | null): void {
    const current = nextJob === undefined ? job : nextJob;
    const dismiss = contactImportDialogDismissAction(current?.status);
    const keepBackgroundState = dismiss === "keep-running";
    if (keepBackgroundState && current) {
      pendingProgressToastRevealRef.current = current;
      onClose();
      return;
    }
    if (dismiss === "abandon-draft" && current) {
      void abandonDraftThen(current, onClose);
      return;
    }
    resetDraftState();
    onClose();
  }

  function handleStartNewImport(): void {
    resetDraftState();
    setDiscardRestoreJob(true);
    setActiveCheckSettled(true);
    onClearRestoreJobId?.();
  }

  async function handleDownloadTemplate(): Promise<void> {
    setError(null);
    try {
      const response = await fetch(contactImportTemplateUrl(organizationId), {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(t("contacts.import.templateError"));
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "contacts-import-template.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("contacts.import.templateError"));
    }
  }

  async function handleFile(file: File): Promise<void> {
    const epoch = draftEpochRef.current;
    const previous = job;
    let replacedDraft = false;
    setLoading(true);
    setPreviewPending(true);
    setUploadingFileName(file.name);
    setReturnedToSetup(false);
    setError(null);
    try {
      if (previous && isContactImportJobPreviewStatus(previous.status)) {
        await cancelContactImportJob(organizationId, previous.id);
        replacedDraft = true;
      }
      if (draftEpochRef.current !== epoch) return;
      const envelope = await uploadContactImportFile(organizationId, file);
      if (draftEpochRef.current !== epoch) {
        if (isContactImportJobPreviewStatus(envelope.job.status)) {
          await cancelContactImportJob(organizationId, envelope.job.id).catch(() => undefined);
        }
        return;
      }
      applyJobToForm(envelope.job);
    } catch (err) {
      if (draftEpochRef.current !== epoch) return;
      setPreviewPending(false);
      if (replacedDraft) {
        resetDraftState();
      }
      setError(mapContactError(t, err, t("contacts.import.previewError")));
    } finally {
      if (inputRef.current) inputRef.current.value = "";
      if (draftEpochRef.current === epoch) {
        setLoading(false);
        setUploadingFileName(null);
      }
    }
  }

  async function handleConfirmImport(): Promise<void> {
    if (
      !job ||
      !contactImportMappingHasTarget(mapping) ||
      !contactImportCanExecute(capacity, capacityLoading) ||
      birthdayChoiceMissing
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const confirmed = await confirmContactImportJob(organizationId, job.id, {
        columnMapping: mapping,
        birthdayFormat,
        defaultCountryIso,
        autoLinkExactPhoneMatches: autoLink,
        applyGroupIds: applyGroups.groupIds.length > 0 ? applyGroups.groupIds : undefined,
      });
      applyJobToForm(confirmed);
      dispatchContactImportWatch(confirmed.id);
      onJobStarted?.();
      handleClose(confirmed);
    } catch (err) {
      if (isContactLimitReachedError(err)) {
        const copy = contactLimitFeedback(t, "import");
        notifications?.pushNotification({
          kind: "error",
          message: copy.title,
          detail: copy.message,
        });
        setError(copy.message);
      } else {
        setError(mapContactError(t, err, t("contacts.import.error")));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelJob(): Promise<void> {
    if (!job || loading || job.status === "CANCEL_REQUESTED") return;
    setLoading(true);
    try {
      const cancelled = await cancelContactImportJob(organizationId, job.id);
      applyJobToForm(cancelled);
    } catch (err) {
      setError(mapContactError(t, err, t("contacts.import.error")));
    } finally {
      setLoading(false);
    }
  }

  function handleDiscardPreparedFile(): void {
    if (job && isContactImportJobPreviewStatus(job.status)) {
      void abandonDraftThen(job);
      return;
    }
    resetDraftState();
  }

  const loadingCopy = previewPending
    ? job?.status === "PREPARING"
      ? t("contacts.import.job.status.PREPARING")
      : t("contacts.import.readingFile")
    : t("contacts.import.preparingImport");

  const summaryIcon =
    job?.status === "FAILED" ? (
      <CircleX size={22} strokeWidth={2.25} aria-hidden="true" />
    ) : job?.status === "CANCELLED" ? (
      <Ban size={20} strokeWidth={2.25} aria-hidden="true" />
    ) : (
      <CircleCheck size={22} strokeWidth={2.25} aria-hidden="true" />
    );

  const summaryIconClass =
    job?.status === "FAILED"
      ? "bg-error-muted text-destructive"
      : job?.status === "CANCELLED"
        ? "bg-surface-muted text-muted"
        : "bg-success-muted text-success";

  useEffect(() => {
    if (wizard !== "review" || capacityLoading || canExecuteImport) return;
    setWizardStep("map");
  }, [wizard, capacityLoading, canExecuteImport]);

  const stepFocusKey = wizard ?? dialogView;
  const previousFocusKey = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      previousFocusKey.current = null;
      return;
    }
    if (previousFocusKey.current == null) {
      previousFocusKey.current = stepFocusKey;
      return;
    }
    if (previousFocusKey.current === stepFocusKey) return;
    previousFocusKey.current = stepFocusKey;
    stepTitleRef.current?.focus();
  }, [open, stepFocusKey]);

  function selectWizardStep(step: ImportWizardStep): void {
    switch (step) {
      case "file":
        setWizardStep("file");
        setReturnedToSetup(true);
        return;
      case "review":
        if (!canExecuteImport || quotaExceeded) return;
        setReturnedToSetup(false);
        setWizardStep("review");
        return;
      case "map":
        if (!preparedAwaiting && dialogView !== "map") return;
        setReturnedToSetup(false);
        setWizardStep("map");
        return;
      default: {
        const _exhaustive: never = step;
        return _exhaustive;
      }
    }
  }

  const stepTitle =
    wizard === "file"
      ? t("contacts.import.steps.file")
      : wizard === "map"
        ? t("contacts.import.mapping.title")
        : wizard === "review"
          ? t("contacts.import.review.title")
          : dialogView === "progress"
            ? t("contacts.import.job.status.PROCESSING")
            : dialogView === "summary" && job
              ? t(`contacts.import.job.status.${job.status}`)
              : t("contacts.import.title");

  const completedSummary = dialogView === "summary" && job?.status === "COMPLETED";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <ContactsModalContent className="w-[min(96vw,48rem)]">
        <div className={contactsModalHeaderClass}>
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 inline-grid size-8 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface text-accent"
              aria-hidden="true"
            >
              <ArrowDownToLine size={15} />
            </span>
            <div className="min-w-0 flex-1 space-y-2">
              <AlertDialogTitle>{t("contacts.import.title")}</AlertDialogTitle>
              {wizard ? (
                <ImportContactsStepper
                  active={wizard}
                  mapUnlocked={preparedAwaiting || dialogView === "map"}
                  reviewUnlocked={settledCanExecute && !capacityLoading}
                  onSelect={selectWizardStep}
                />
              ) : null}
              {completedSummary ? null : (
                <h2
                  ref={stepTitleRef}
                  tabIndex={-1}
                  className="m-0 text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground"
                >
                  {stepTitle}
                </h2>
              )}
              {dialogView === "setup" ? (
                <>
                  <p className="m-0 text-[0.8125rem] leading-snug text-muted">
                    {t("contacts.import.help")}
                  </p>
                  <p className="m-0 text-[0.6875rem] leading-snug text-muted">
                    {t("contacts.import.columnsHint")}
                  </p>
                </>
              ) : null}
            </div>
          </div>
          {fileName && dialogView === "map" ? (
            <ImportSelectedFileBar
              fileName={fileName}
              rowCount={rowCount}
              disabled={loading}
              onChangeFile={() => inputRef.current?.click()}
              onRemoveFile={handleDiscardPreparedFile}
            />
          ) : null}
        </div>

        <div className={contactsModalBodyClass} data-contact-import-scroll="">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          {fileName && dialogView === "setup" ? (
            <div className="mb-4">
              <ImportSelectedFileBar
                fileName={fileName}
                rowCount={rowCount}
                disabled={loading}
                onChangeFile={() => inputRef.current?.click()}
                onRemoveFile={handleDiscardPreparedFile}
              />
            </div>
          ) : null}
          {dialogView === "loading" ? (
            <div className="grid gap-2 py-4" data-testid="contact-import-dialog-loading" role="status">
              {uploadingFileName ? (
                <p className="m-0 truncate text-[0.8125rem] font-medium text-foreground" dir="auto">
                  {uploadingFileName}
                </p>
              ) : null}
              <div className="h-0.5 overflow-hidden rounded-full bg-surface-muted">
                <div className="h-full w-full origin-left animate-pulse bg-accent motion-reduce:animate-none rtl:origin-right" />
              </div>
              <p className="m-0 text-[0.8125rem] text-muted">{loadingCopy}</p>
            </div>
          ) : null}

          {dialogView === "setup" ? (
            <div className="space-y-4">
              {fileName ? null : (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => inputRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-surface-muted/40 px-4 py-5 text-[0.8125rem] font-medium text-foreground transition-colors not-disabled:hover:bg-surface-muted disabled:opacity-70"
                  data-testid="contact-import-choose-file"
                >
                  <FileUp size={16} aria-hidden="true" />
                  {loading ? t("contacts.import.readingFile") : t("contacts.import.chooseFile")}
                </button>
              )}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-0 py-1 text-[0.75rem] font-medium text-muted not-disabled:hover:text-foreground"
                onClick={() => void handleDownloadTemplate()}
              >
                <ArrowDownToLine size={14} aria-hidden="true" />
                {t("contacts.import.downloadTemplate")}
              </Button>
            </div>
          ) : null}

          {dialogView === "map" && wizard === "review" ? (
            <ImportContactsReviewStep
              headers={headers}
              mapping={mapping}
              phoneUnmapped={phoneUnmapped}
              capacity={capacity}
              capacityLoading={capacityLoading}
              autoLink={autoLink}
              summaryAutoLink={capacityAutoLink}
              rowCount={rowCount}
              countryIso={defaultCountryIso}
              countryLabel={countryLabel}
              groups={groups}
              applyGroups={applyGroups}
              onChangeApplyGroups={setApplyGroups}
              onGroupsChanged={onGroupsChanged}
              organizationId={organizationId}
              onEditMapping={() => setWizardStep("map")}
              disabled={loading}
            />
          ) : null}

          {dialogView === "map" && wizard === "map" ? (
            <div className="grid gap-4">
              <ImportContactsOutcomePanel
                capacity={capacity}
                capacityLoading={capacityLoading}
                autoLink={capacityAutoLink}
                rowCount={rowCount}
              />
              <section
                className="grid gap-4 rounded-lg border border-line bg-surface px-3 py-3 text-start sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start sm:gap-x-5"
                aria-labelledby="contact-import-settings-title"
                data-testid="contact-import-settings"
              >
                <h3 id="contact-import-settings-title" className="sr-only">
                  {t("contacts.import.settingsTitle")}
                </h3>
                <div className="grid gap-1.5 text-start">
                  <div className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 text-[0.75rem] font-medium text-foreground">
                      {t("contacts.import.defaultCountry")}
                    </span>
                    <SettingHelp
                      label={t("contacts.import.defaultCountryHelpLabel")}
                      body={t("contacts.import.defaultCountryHelp")}
                    />
                  </div>
                  <CountryIsoSelector
                    value={defaultCountryIso}
                    onChange={setDefaultCountryIso}
                    disabled={loading}
                  />
                </div>
                <div
                  aria-hidden="true"
                  className="mx-auto h-px w-10 bg-line sm:mx-0 sm:h-8 sm:w-px sm:self-center"
                />
                {phoneUnmapped ? (
                  <div
                    className="flex items-start gap-2 text-start"
                    data-testid="contact-import-phone-unmapped"
                  >
                    <Info size={15} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" />
                    <div className="grid min-w-0 gap-1">
                      <p className="m-0 text-[0.8125rem] font-medium text-foreground">
                        {t("contacts.import.capacity.phoneUnmappedTitle")}
                      </p>
                      <p className="m-0 text-[0.75rem] leading-snug text-muted">
                        {t("contacts.import.capacity.phoneUnmappedBody")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-1.5 text-start">
                    <div className="flex items-center gap-1.5">
                      <ChannelIcon
                        platformType="WHATSAPP"
                        size={14}
                        className="shrink-0 text-whatsapp"
                      />
                      <span
                        id="contact-import-auto-link-label"
                        className="min-w-0 flex-1 text-[0.75rem] font-medium text-foreground"
                      >
                        {t("contacts.import.autoLink")}
                      </span>
                      <SettingHelp
                        label={t("contacts.import.autoLinkHelpLabel")}
                        body={t("contacts.import.autoLinkHelp")}
                      />
                    </div>
                    <label className="flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-[0.8125rem]">
                      <input
                        type="checkbox"
                        checked={autoLink}
                        onChange={(event) => setAutoLink(event.target.checked)}
                        className="size-4 shrink-0"
                        data-testid="import-auto-link-switch"
                        aria-labelledby="contact-import-auto-link-label"
                      />
                      <span>
                        {autoLink
                          ? t("contacts.import.autoLinkOn")
                          : t("contacts.import.autoLinkOff")}
                      </span>
                    </label>
                  </div>
                )}
              </section>
              <div>
                <ImportContactsMappingStep
                  headers={headers}
                  samples={samples}
                  sampleRows={job?.preview?.sampleRows ?? []}
                  mapping={mapping}
                  onChangeMapping={(next) => {
                    setMapping(next);
                    setWizardStep("map");
                    setCapacity(null);
                    setCapacityLoading(contactImportMappingHasTarget(next));
                  }}
                  birthdayFormat={birthdayFormat}
                  onChangeBirthdayFormat={setBirthdayFormat}
                  birthdayAmbiguous={capacity?.birthdayImport?.ambiguous === true}
                  disabled={loading}
                />
              </div>
            </div>
          ) : null}

          {dialogView === "progress" && job ? (
            <div className="space-y-4" data-testid="contact-import-job-progress">
              <div className="space-y-1">
                <p className="m-0 text-[0.875rem] font-medium text-foreground">
                  {t(`contacts.import.job.status.${job.status}`)}
                </p>
                {job.totalRows != null ? (
                  <p className="m-0 text-[0.8125rem] text-muted" data-testid="contact-import-processed-line">
                    {t("contacts.import.job.processedOfTotal", {
                      processed: job.processedRows.toLocaleString(i18n.language),
                      total: job.totalRows.toLocaleString(i18n.language),
                    })}
                    {" · "}
                    {t("contacts.import.job.percent", {
                      percent: contactImportJobPercent(job.processedRows, job.totalRows),
                    })}
                  </p>
                ) : null}
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-surface-muted"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={
                  job.totalRows != null
                    ? contactImportJobPercent(job.processedRows, job.totalRows)
                    : undefined
                }
                aria-label={t(`contacts.import.job.status.${job.status}`)}
              >
                <div
                  className={cn(
                    "h-full bg-success",
                    job.totalRows == null && "w-full animate-pulse motion-reduce:animate-none",
                  )}
                  style={
                    job.totalRows != null
                      ? { width: `${contactImportJobPercent(job.processedRows, job.totalRows)}%` }
                      : undefined
                  }
                />
              </div>
              <ImportJobResultStats
                created={job.createdCount}
                restored={job.restoredCount}
                skipped={job.skippedCount}
                failed={job.failedCount}
              />
            </div>
          ) : null}

          {dialogView === "summary" && job ? (
            <div className="space-y-4" data-testid="contact-import-job-summary">
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "inline-grid size-10 shrink-0 place-items-center rounded-full",
                    summaryIconClass,
                  )}
                >
                  {summaryIcon}
                </span>
                <div className="min-w-0 space-y-1">
                  {completedSummary ? (
                    <h2
                      ref={stepTitleRef}
                      tabIndex={-1}
                      className="m-0 text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground"
                    >
                      {t(`contacts.import.job.status.${job.status}`)}
                    </h2>
                  ) : (
                    <p className="m-0 text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground">
                      {job.status === "FAILED" && job.errorCode === "CONTACT_LIMIT_REACHED"
                        ? contactLimitFeedback(t, "import").title
                        : t(`contacts.import.job.status.${job.status}`)}
                    </p>
                  )}
                  {fileName ? (
                    <ImportFileMetadata fileName={fileName} rowCount={rowCount} />
                  ) : null}
                </div>
              </div>
              {importJobShowsResultCounters(job) ? (
                <ImportJobResultStats
                  created={job.createdCount}
                  restored={job.restoredCount}
                  skipped={job.skippedCount}
                  failed={job.failedCount}
                />
              ) : null}
              {job.status === "COMPLETED" && (job.preview?.birthdaySkippedCount ?? 0) > 0 ? (
                <p
                  className="m-0 text-[0.8125rem] leading-snug text-muted"
                  data-testid="contact-import-birthday-skipped"
                >
                  {t("contacts.import.job.birthdaySkipped", {
                    count: job.preview?.birthdaySkippedCount ?? 0,
                  })}
                </p>
              ) : null}
              {job.status === "FAILED" ? (
                <p className="m-0 text-[0.8125rem] text-red-600">
                  {job.errorCode === "CONTACT_LIMIT_REACHED"
                    ? contactLimitFeedback(t, "import").message
                    : job.errorCode === "OBJECT_STORAGE_UNAVAILABLE"
                      ? t("contacts.import.errors.UPLOAD_UNAVAILABLE")
                      : t("contacts.import.job.failedToast")}
                </p>
              ) : null}
            </div>
          ) : null}

          {error ? <p className="text-[0.8125rem] text-red-600">{error}</p> : null}
        </div>

        <div className={contactsModalFooterClass}>
          {wizard === "map" || wizard === "review" ? (
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => selectWizardStep(wizard === "review" ? "map" : "file")}
            >
              {t("contacts.import.back")}
            </Button>
          ) : null}
          {wizard === "map" && settledCanExecute && !birthdayChoiceMissing ? (
            <Button
              size="sm"
              disabled={loading || capacityLoading}
              onClick={() => selectWizardStep("review")}
            >
              {t("contacts.import.reviewImport")}
            </Button>
          ) : null}
          {wizard === "review" && canExecuteImport ? (
            <Button
              size="sm"
              disabled={
                loading ||
                !contactImportMappingHasTarget(mapping) ||
                !job ||
                birthdayChoiceMissing
              }
              data-testid="contact-import-confirm"
              onClick={() => void handleConfirmImport()}
            >
              {loading ? t("contacts.import.loading") : t("contacts.import.importNow")}
            </Button>
          ) : null}
          {showCancelRemaining ? (
            <Button
              variant="destructive"
              size="sm"
              disabled={loading}
              onClick={() => void handleCancelJob()}
              data-testid="contact-import-job-cancel"
            >
              {loading ? (
                <LoaderCircle
                  size={14}
                  className="animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : null}
              {t("contacts.import.job.cancel")}
            </Button>
          ) : null}
          {dialogView === "summary" && job?.status === "FAILED" ? (
            <Button variant="outline" size="sm" disabled={loading} onClick={handleStartNewImport}>
              {t("contacts.import.job.retry")}
            </Button>
          ) : null}
          {dialogView === "summary" && job?.status !== "FAILED" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartNewImport}
              data-testid="contact-import-new-file"
            >
              {t("contacts.import.newImport")}
            </Button>
          ) : null}
          <Button
            variant={dialogView === "summary" ? undefined : "outline"}
            size="sm"
            onClick={() => handleClose()}
            data-testid="contact-import-dialog-dismiss"
          >
            {dialogView === "progress"
              ? t("contacts.import.job.minimize")
              : dialogView === "summary"
                ? t("common.close")
                : t("common.cancel")}
          </Button>
          {dialogView === "setup" && preparedAwaiting ? (
            <Button
              type="button"
              size="sm"
              disabled={loading}
              onClick={() => setReturnedToSetup(false)}
              data-testid="contact-import-continue-mapping"
            >
              {t("contacts.import.continueToMapping")}
            </Button>
          ) : null}
        </div>
      </ContactsModalContent>
    </AlertDialog>
  );
}
